// src/components/PropagateTasksModal.tsx
// Gap 1 — Drawing task → ConfiguredTask bridge review modal
//
// PM lands here after clicking "Push to mobile" on an APPROVED DrawingTask.
// The modal shows the auto-populated ConfiguredTask draft and lets the PM:
//   ① Edit any field before final push
//   ② See a warning if required materials are not yet ON_SITE
//   ③ Confirm → commitDraftTask() fires and ConfiguredTask is created
//
// Usage (in Drawings.tsx TasksTab):
//   {propagateDraft && (
//     <PropagateTasksModal
//       draft={propagateDraft}
//       onCommit={(committed) => handleCommitDraft(committed)}
//       onClose={() => setPropagateDraft(null)}
//     />
//   )}
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import {
  X, CheckCircle, AlertTriangle, Package, Smartphone,
  Zap, Camera, Info,
} from 'lucide-react'
import { PropagatedTaskDraft, commitDraftTask, materialAvailabilityWarning } from '../utils/propagateTasks'
import { ConfiguredTask, MATERIAL_STOCK_META } from '../utils/mockDataMobile'
import { MOCK_EMPLOYEES } from '../utils/mockDataExtended'

interface PropagateTasksModalProps {
  draft: PropagatedTaskDraft
  onCommit: (task: ConfiguredTask) => void
  onClose: () => void
}

const PHOTO_TIMING_LABELS: Record<ConfiguredTask['photoTiming'], string> = {
  BEFORE:               'Before only',
  DURING:               'During only',
  COMPLETION:           'Completion only',
  BEFORE_AFTER:         'Before + after',
  BEFORE_DURING_AFTER:  'Before + during + after',
}

export function PropagateTasksModal({ draft, onCommit, onClose }: PropagateTasksModalProps) {
  // Editable fields — PM may override any before committing
  const [title, setTitle]             = useState(draft.title)
  const [description, setDesc]        = useState(draft.description)
  const [specRef, setSpecRef]         = useState(draft.specClauseRef ?? '')
  const [photoReq, setPhotoReq]       = useState(draft.photoRequirements)
  const [photoTiming, setPhotoTiming] = useState<ConfiguredTask['photoTiming']>(draft.photoTiming)
  const [holdPoint, setHoldPoint]     = useState(draft.holdPointLinkage ?? '')
  const [assignedToId, setAssigned]   = useState(draft.assignedToId ?? '')
  const [targetDate, setTargetDate]   = useState(draft.targetDate ?? '')
  const [committed, setCommitted]     = useState(false)

  const matWarnings = materialAvailabilityWarning(draft)
  const hasWarnings = matWarnings.length > 0

  // Close on Escape
  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [onClose])

  const handleCommit = () => {
    const emp = MOCK_EMPLOYEES.find(e => e.id === assignedToId)
    const updated: PropagatedTaskDraft = {
      ...draft,
      title,
      description,
      specClauseRef:     specRef || null,
      photoRequirements: photoReq,
      photoTiming,
      holdPointLinkage:  holdPoint || null,
      assignedToId:      assignedToId || null,
      assignedToName:    emp?.fullName ?? draft.assignedToName,
      targetDate:        targetDate || null,
    }
    const result = commitDraftTask(updated)
    onCommit(result)
    setCommitted(true)
  }

  // ── Success screen ─────────────────────────────────────────────
  if (committed) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <Smartphone className="w-8 h-8 text-teal-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">Task pushed to Mobile Comms</h3>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-semibold">{title}</span> is now live in the Mobile Comms task list.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Field crew will see this task on their devices the next time they sync.
          </p>
          {hasWarnings && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 text-left">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
              {matWarnings.length} material{matWarnings.length !== 1 ? 's' : ''} not yet on site —
              crew will see a materials warning on their task card.
            </div>
          )}
          <button onClick={onClose} className="mt-5 btn-primary w-full">Done</button>
        </div>
      </div>
    )
  }

  // ── Review modal ───────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-teal-600" />
              <h2 className="font-bold text-gray-900">Review AI-propagated task</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              Auto-populated from drawing task — review, edit if needed, then push to crew devices
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">

          {/* Materials availability warning */}
          {hasWarnings && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    {matWarnings.length} required material{matWarnings.length !== 1 ? 's' : ''} not yet on site
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Crew will see a materials warning on their task card.
                    Consider delaying push until materials arrive, or proceed and advise crew directly.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {matWarnings.map(m => {
                      const meta = MATERIAL_STOCK_META[m.stockStatus]
                      return (
                        <span key={m.bomLineId} className={`text-xs px-2 py-0.5 rounded-full font-medium ${meta.bg} ${meta.text}`}>
                          {m.description.slice(0, 35)}{m.description.length > 35 ? '…' : ''} — {meta.label}
                        </span>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI source info */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800">
              Fields below were auto-populated from drawing task <strong>{draft.drawingTaskId}</strong>.
              AI resolved: hold point, spec clause ref, BOM materials, and photo requirements.
              Edit any field before confirming the push.
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Task title <span className="text-red-500">*</span>
            </label>
            <input className="input text-sm w-full" value={title} onChange={e => setTitle(e.target.value)} />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Description / instructions for crew
            </label>
            <textarea
              className="input text-sm w-full h-20 resize-none"
              value={description}
              onChange={e => setDesc(e.target.value)}
            />
          </div>

          {/* Spec ref + hold point */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Spec clause reference</label>
              <input
                className="input text-sm"
                value={specRef}
                onChange={e => setSpecRef(e.target.value)}
                placeholder="e.g. MRTS50 cl. 6.3.4"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Hold point
                {holdPoint && <span className="ml-1 text-xs text-orange-600 font-normal">(blocks crew until cleared)</span>}
              </label>
              <input
                className="input text-sm"
                value={holdPoint}
                onChange={e => setHoldPoint(e.target.value)}
                placeholder="e.g. HP-001 (or leave blank)"
              />
            </div>
          </div>

          {/* Photo count + timing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                <Camera className="w-3 h-3 inline mr-1" />Photos required
              </label>
              <input
                type="number" min={0} max={10}
                className="input text-sm"
                value={photoReq}
                onChange={e => setPhotoReq(parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Photo timing</label>
              <select
                className="input text-sm"
                value={photoTiming}
                onChange={e => setPhotoTiming(e.target.value as ConfiguredTask['photoTiming'])}
              >
                {(Object.entries(PHOTO_TIMING_LABELS) as [ConfiguredTask['photoTiming'], string][]).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Assign to + target date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Assign to</label>
              <select className="input text-sm" value={assignedToId} onChange={e => setAssigned(e.target.value)}>
                <option value="">All crew on job</option>
                {MOCK_EMPLOYEES.filter(e => (e as any).active !== false).map(e => (
                  <option key={e.id} value={e.id}>{e.fullName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Target date</label>
              <input type="date" className="input text-sm" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
            </div>
          </div>

          {/* Materials required panel */}
          {draft.materialsRequired && draft.materialsRequired.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b bg-gray-50 flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs font-semibold text-gray-700">
                  Required materials ({draft.materialsRequired.length}) — shown on crew task card
                </span>
              </div>
              <div className="divide-y divide-gray-50">
                {draft.materialsRequired.map(m => {
                  const meta = MATERIAL_STOCK_META[m.stockStatus]
                  return (
                    <div key={m.bomLineId} className="px-4 py-2.5 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-gray-900 truncate">{m.description}</div>
                        {m.specRef && <div className="text-xs text-gray-400">{m.specRef}</div>}
                      </div>
                      <div className="text-xs text-gray-500 flex-shrink-0">{m.qty} {m.unit}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${meta.bg} ${meta.text}`}>
                        {meta.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* AI resolution summary */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-blue-500" />
              Auto-resolved from drawing task
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {[
                ['Hold point',       holdPoint || 'None'],
                ['Spec clause',      specRef || 'Not specified'],
                ['Discipline',       draft.discipline],
                ['Photo timing',     PHOTO_TIMING_LABELS[photoTiming]],
                ['BOM items linked', String(draft.materialsRequired?.length ?? 0)],
                ['Drawing task ID',  draft.drawingTaskId],
              ].map(([l, v]) => (
                <div key={l}>
                  <div className="text-gray-400 font-medium">{l}</div>
                  <div className="text-gray-700 mt-0.5">{v}</div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex gap-3 sticky bottom-0">
          <button onClick={onClose} className="btn-secondary flex-shrink-0">Cancel</button>
          <button
            onClick={handleCommit}
            disabled={!title.trim()}
            className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Smartphone className="w-4 h-4" />
            Confirm &amp; push to Mobile Comms
          </button>
        </div>

      </div>
    </div>
  )
}
